import { Plus, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Input, PrimaryButton } from '../ui';
import { getAllSites } from '../SiteManagement/siteApi';
import { normalizeSites } from '../QR/qrFacilityData';
import { lostAndFoundApi } from '../LostAndFound/lostAndFoundApi';
import { findBlockIdForFlat } from './duesDisplay';
import { cacheDueForSite, duesApi, getDuesSiteId, setDuesSiteId } from './duesApi';
import {
  DUE_CATEGORY_OPTIONS,
  normalizeCategory,
  toApiLineItems,
} from './duesUtils';

const formatCurrency = (amount) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(amount || 0));

const emptyLineItem = () => ({
  id: `li_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  name: '',
  quantity: 1,
  unit: '',
  price_per_unit: '',
  subtotal: 0,
});

const lineItemsFromDue = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [emptyLineItem()];
  }
  const mapped = items.map((li, index) => {
    const quantity = Number(li.quantity) || 1;
    const pricePerUnit = Number(li.price_per_unit ?? li.pricePerUnit ?? 0);
    return {
      id: li.id || li.line_item_id || `li_prefill_${index}_${Date.now()}`,
      name: li.name || li.itemName || '',
      quantity,
      unit: li.unit || li.units || '',
      price_per_unit:
        li.price_per_unit ?? li.pricePerUnit ?? (pricePerUnit > 0 ? pricePerUnit : ''),
      subtotal: Number(li.subtotal ?? quantity * pricePerUnit) || 0,
    };
  });
  return mapped;
};

const CreateDue = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const editingDue = routerLocation.state?.due;
  const isEditMode = Boolean(editingDue);
  const prefillDoneRef = useRef(false);

  const [location, setLocation] = useState({
    site: getDuesSiteId() || '',
    block: '',
    flat: '',
  });

  const [sites, setSites] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [flats, setFlats] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [loadingFlats, setLoadingFlats] = useState(false);
  const [siteError, setSiteError] = useState('');
  const [blockError, setBlockError] = useState('');
  const [flatError, setFlatError] = useState('');

  const [info, setInfo] = useState({
    category: '',
    title: '',
    description: '',
  });

  const [lineItems, setLineItems] = useState([emptyLineItem()]);
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);
  const [draftLineItem, setDraftLineItem] = useState(emptyLineItem());
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const totalAmount = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const price = Number(item.price_per_unit) || 0;
      return sum + qty * price;
    }, 0);
  }, [lineItems]);

  const withSubtotal = (item) => {
    const quantity = Number(item.quantity) || 0;
    const pricePerUnit = Number(item.price_per_unit) || 0;
    return {
      ...item,
      quantity,
      price_per_unit: item.price_per_unit,
      subtotal: quantity * pricePerUnit,
    };
  };

  useEffect(() => {
    let active = true;
    const loadSites = async () => {
      setLoadingSites(true);
      setSiteError('');
      try {
        const res = await getAllSites();
        const normalized = normalizeSites(res).sort((a, b) => {
          const aName = String(a?.site_name || a?.name || '').toLowerCase();
          const bName = String(b?.site_name || b?.name || '').toLowerCase();
          return aName.localeCompare(bName);
        });
        if (!active) return;
        setSites(normalized);
      } catch (e) {
        if (!active) return;
        setSites([]);
        setSiteError(e?.message || 'Failed to load sites.');
      } finally {
        if (!active) return;
        setLoadingSites(false);
      }
    };
    loadSites();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!location.site) {
      setBlocks([]);
      setFlats([]);
      setBlockError('');
      setFlatError('');
      return;
    }

    let active = true;
    const loadBlocks = async () => {
      setLoadingBlocks(true);
      setBlockError('');
      setBlocks([]);
      setFlats([]);
      setFlatError('');
      try {
        const list = await lostAndFoundApi.getBlocks(location.site);
        if (!active) return;
        setBlocks(list);
        if (!list.length) setBlockError('No blocks found for this site.');
      } catch (e) {
        if (!active) return;
        setBlocks([]);
        setBlockError(e?.message || 'Failed to load blocks.');
      } finally {
        if (!active) return;
        setLoadingBlocks(false);
      }
    };

    loadBlocks();
    return () => {
      active = false;
    };
  }, [location.site]);

  useEffect(() => {
    if (!location.site || !location.block) {
      setFlats([]);
      setFlatError('');
      return;
    }

    let active = true;
    const loadFlats = async () => {
      setLoadingFlats(true);
      setFlatError('');
      setFlats([]);
      try {
        const list = await lostAndFoundApi.getFlats(location.site, location.block);
        if (!active) return;
        setFlats(list);
        if (!list.length) setFlatError('No flats found for this block.');
      } catch (e) {
        if (!active) return;
        setFlats([]);
        setFlatError(e?.message || 'Failed to load flats.');
      } finally {
        if (!active) return;
        setLoadingFlats(false);
      }
    };

    loadFlats();
    return () => {
      active = false;
    };
  }, [location.site, location.block]);

  useEffect(() => {
    const due = editingDue;
    if (!due || prefillDoneRef.current) return;

    prefillDoneRef.current = true;

    const siteId = due.site_id || due.siteId || getDuesSiteId() || '';
    const flatId = due.flat_id || due.flatId || '';
    const blockId = due.block_id || due.blockId || '';

    setInfo({
      category: normalizeCategory(due.category || due.type || ''),
      title: due.title || '',
      description: due.description || '',
    });
    setLineItems(lineItemsFromDue(due.line_items || due.lineItems));

    if (siteId) {
      setDuesSiteId(siteId);
      setLocation({
        site: siteId,
        block: blockId,
        flat: flatId,
      });
    }
  }, [editingDue]);

  useEffect(() => {
    if (!isEditMode || !editingDue || location.block || !location.site || !location.flat) {
      return;
    }

    let active = true;
    const resolveBlock = async () => {
      const blockId = await findBlockIdForFlat(location.site, location.flat);
      if (!active || !blockId) return;
      setLocation((p) => ({ ...p, block: blockId }));
    };

    resolveBlock();
    return () => {
      active = false;
    };
  }, [isEditMode, editingDue, location.site, location.flat, location.block]);

  const updateLineItem = (id, patch) => {
    setLineItems((prev) =>
      prev.map((li) => (li.id === id ? withSubtotal({ ...li, ...patch }) : li))
    );
  };

  const openAddLineItem = () => {
    setDraftLineItem(emptyLineItem());
    setIsLineItemModalOpen(true);
  };

  const closeAddLineItem = () => {
    setIsLineItemModalOpen(false);
    setDraftLineItem(emptyLineItem());
  };

  const confirmAddLineItem = (e) => {
    e.preventDefault();
    const normalized = {
      ...draftLineItem,
      name: (draftLineItem.name || '').trim(),
      unit: (draftLineItem.unit || '').trim(),
      quantity: Number(draftLineItem.quantity) || 0,
      price_per_unit: Number(draftLineItem.price_per_unit) || 0,
    };

    if (!normalized.name) {
      alert('Please enter Item Name.');
      return;
    }
    if (!Number.isFinite(normalized.quantity) || normalized.quantity <= 0) {
      alert('Please enter a valid Quantity.');
      return;
    }
    if (!Number.isFinite(normalized.price_per_unit) || normalized.price_per_unit <= 0) {
      alert('Please enter a valid Price per Unit.');
      return;
    }

    setLineItems((prev) => [...prev, withSubtotal(normalized)]);
    closeAddLineItem();
  };

  const removeItem = (id) => {
    setLineItems((prev) => (prev.length <= 1 ? prev : prev.filter((li) => li.id !== id)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!location.site) {
      setSubmitError('Please select a site.');
      return;
    }
    if (!location.flat) {
      setSubmitError('Please select a flat.');
      return;
    }
    if (!(info.category || '').trim()) {
      setSubmitError('Please select a category.');
      return;
    }
    if (!(info.title || '').trim()) {
      setSubmitError('Please enter a title.');
      return;
    }
    if (!Number.isFinite(totalAmount) || totalAmount <= 0) {
      setSubmitError('Please add at least one valid line item.');
      return;
    }

    const apiLineItems = toApiLineItems(lineItems);
    const invalidItem = apiLineItems.find(
      (li) => !li.name || li.quantity <= 0 || li.price_per_unit <= 0
    );
    if (invalidItem) {
      setSubmitError('Each line item needs a name, quantity, and price per unit.');
      return;
    }

    const payload = {
      site_id: location.site,
      flat_id: location.flat,
      currency: 'INR',
      amount: totalAmount,
      title: info.title.trim(),
      description: (info.description || '').trim(),
      category: normalizeCategory(info.category),
      line_items: apiLineItems,
      metadata: { source: 'facility_app' },
    };

    setSubmitting(true);
    try {
      const siteName =
        sites.find((s) => String(s.site_id) === String(location.site))?.site_name ||
        '';
      const blockLabel =
        blocks.find((b) => String(b.id) === String(location.block))?.label || '';
      const flatLabel =
        flats.find((f) => String(f.id) === String(location.flat))?.label || '';

      const res = await duesApi.createFlatDue({
        ...payload,
        block_id: location.block || undefined,
      });
      setDuesSiteId(location.site);
      const createdDue = res?.due || res;
      const createdId =
        createdDue?.due_id || createdDue?.dueId || res?.due_id || res?.id;
      if (createdId) {
        const dueForUi = {
          ...createdDue,
          due_id: createdDue?.due_id || createdId,
          site_id: createdDue?.site_id || location.site,
          flat_id: createdDue?.flat_id || location.flat,
          block_id: createdDue?.block_id || location.block,
          site_name: siteName,
          block_name: blockLabel,
          flat_name: flatLabel,
          flat_label: flatLabel,
        };
        cacheDueForSite(location.site, dueForUi);
        navigate(`/dues/${createdId}`, {
          state: { due: dueForUi },
        });
      } else {
        navigate('/dues');
      }
    } catch (err) {
      setSubmitError(err?.message || 'Failed to create due.');
    } finally {
      setSubmitting(false);
    }
  };

  const onSiteChange = (site) => {
    setDuesSiteId(site);
    setLocation((p) => ({ ...p, site, block: '', flat: '' }));
  };

  const onBlockChange = (block) => {
    setLocation((p) => ({ ...p, block, flat: '' }));
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <div className="space-y-8">
          <div className="rounded-3xl">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {isEditMode ? 'Edit Due' : 'Create Due'}
                </h1>
                <p className="mt-2 text-gray-600">
                  {isEditMode
                    ? 'Update the details below and save'
                    : 'Fill details to create a due'}
                </p>
              </div>
            </div>

            {submitError && (
              <Card className="p-4 mb-6 border border-red-200 bg-red-50 text-red-800">
                {submitError}
              </Card>
            )}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Location + Info */}
              <div className="lg:col-span-2 space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Select Location
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-700">
                        Site <span className="text-red-600">*</span>
                      </label>
                      <select
                        value={location.site}
                        onChange={(e) => onSiteChange(e.target.value)}
                        required
                        className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      >
                        <option value="">
                          {loadingSites ? 'Loading sites…' : 'Select site'}
                        </option>
                        {sites.map((s) => (
                          <option key={s.site_id} value={s.site_id}>
                            {s.site_name}
                          </option>
                        ))}
                      </select>
                      {siteError && (
                        <div className="mt-2 text-xs font-semibold text-red-700">
                          {siteError}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-700">
                        Block
                      </label>
                      <select
                        value={location.block}
                        onChange={(e) => onBlockChange(e.target.value)}
                        disabled={!location.site || loadingBlocks}
                        className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      >
                        <option value="">
                          {!location.site
                            ? 'Select site first'
                            : loadingBlocks
                              ? 'Loading blocks…'
                              : 'Select block'}
                        </option>
                        {blocks.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.label}
                          </option>
                        ))}
                      </select>
                      {blockError && (
                        <div className="mt-2 text-xs font-semibold text-red-700">
                          {blockError}
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-700">
                        Flat
                      </label>
                      <select
                        value={location.flat}
                        onChange={(e) => setLocation((p) => ({ ...p, flat: e.target.value }))}
                        disabled={!location.site || !location.block || loadingFlats}
                        className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      >
                        <option value="">
                          {!location.site
                            ? 'Select site first'
                            : !location.block
                              ? 'Select block first'
                              : loadingFlats
                                ? 'Loading flats…'
                                : 'Select flat'}
                        </option>
                        {flats.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                      {flatError && (
                        <div className="mt-2 text-xs font-semibold text-red-700">
                          {flatError}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Info</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-semibold text-gray-700">
                        Category <span className="text-red-600">*</span>
                      </label>
                      <select
                        value={info.category}
                        onChange={(e) => setInfo((p) => ({ ...p, category: e.target.value }))}
                        required
                        className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      >
                        <option value="">Select category</option>
                        {DUE_CATEGORY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-semibold text-gray-700">
                        Title <span className="text-red-600">*</span>
                      </label>
                      <Input
                        placeholder="Enter Title"
                        value={info.title}
                        onChange={(e) => setInfo((p) => ({ ...p, title: e.target.value }))}
                        className="min-h-[48px]"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-2 block text-sm font-semibold text-gray-700">
                        Description
                      </label>
                      <textarea
                        value={info.description}
                        onChange={(e) =>
                          setInfo((p) => ({ ...p, description: e.target.value }))
                        }
                        placeholder="Enter description"
                        className="w-full min-h-[120px] rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                      />
                    </div>
                  </div>
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
                    <button
                      type="button"
                      onClick={openAddLineItem}
                      className="inline-flex items-center gap-2 rounded-xl border border-dashed border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add Item
                    </button>
                  </div>

                  {/* Mobile: stacked cards */}
                  <div className="md:hidden space-y-3">
                    {lineItems.map((li, idx) => (
                      <div
                        key={li.id}
                        className="rounded-2xl border border-gray-200 bg-white p-4"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-sm font-semibold text-gray-900">
                            Item {idx + 1}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(li.id)}
                            className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-2 text-red-700 hover:bg-red-100"
                            aria-label="Remove"
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-600">
                              Name
                            </label>
                            <Input
                              value={li.name}
                              onChange={(e) =>
                                updateLineItem(li.id, { name: e.target.value })
                              }
                              placeholder="Maintenance"
                              className="min-h-[44px]"
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-600">
                                Qty
                              </label>
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={li.quantity}
                                onChange={(e) =>
                                  updateLineItem(li.id, { quantity: e.target.value })
                                }
                                className="min-h-[44px]"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-600">
                                Unit
                              </label>
                              <Input
                                value={li.unit}
                                onChange={(e) =>
                                  updateLineItem(li.id, { unit: e.target.value })
                                }
                                placeholder="month"
                                className="min-h-[44px]"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-600">
                                Price / Unit
                              </label>
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={li.price_per_unit}
                                onChange={(e) =>
                                  updateLineItem(li.id, {
                                    price_per_unit: e.target.value,
                                  })
                                }
                                className="min-h-[44px] text-right"
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-600">
                                Subtotal
                              </label>
                              <div className="min-h-[44px] rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 flex items-center justify-end">
                                {formatCurrency(li.subtotal)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Desktop: table */}
                  <div className="hidden md:block overflow-x-auto rounded-2xl border border-gray-200">
                    <table className="min-w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left text-gray-600">
                          <th className="px-4 py-3 font-semibold min-w-[220px]">Item Name</th>
                          <th className="px-4 py-3 font-semibold min-w-[110px]">Qty</th>
                          <th className="px-4 py-3 font-semibold min-w-[130px]">Unit</th>
                          <th className="px-4 py-3 font-semibold min-w-[160px] text-right">
                            Price / Unit
                          </th>
                          <th className="px-4 py-3 font-semibold min-w-[160px] text-right">
                            Subtotal
                          </th>
                          <th className="px-4 py-3 font-semibold min-w-[120px] text-right">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((li) => (
                          <tr key={li.id} className="border-t border-gray-100">
                            <td className="px-4 py-3">
                              <Input
                                value={li.name}
                                onChange={(e) => updateLineItem(li.id, { name: e.target.value })}
                                placeholder="Enter Item Name"
                                className="min-h-[44px]"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={li.quantity}
                                onChange={(e) => updateLineItem(li.id, { quantity: e.target.value })}
                                className="min-h-[44px]"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <Input
                                value={li.unit}
                                onChange={(e) => updateLineItem(li.id, { unit: e.target.value })}
                                placeholder="month"
                                className="min-h-[44px]"
                              />
                            </td>
                            <td className="px-4 py-3 text-right">
                              <Input
                                type="number"
                                min="0"
                                step="1"
                                value={li.price_per_unit}
                                onChange={(e) => updateLineItem(li.id, { price_per_unit: e.target.value })}
                                className="min-h-[44px] text-right"
                              />
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-900">
                              {formatCurrency(li.subtotal)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => removeItem(li.id)}
                                className="inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 p-2 text-red-700 hover:bg-red-100"
                                aria-label="Remove"
                                title="Remove"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>

              {/* Right: Amount */}
              <div className="lg:col-span-1 space-y-6">
                <Card className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Amount</h3>
                  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                    <div className="text-xs font-semibold text-gray-500 mb-1">Total</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {formatCurrency(totalAmount)}
                    </div>
                  </div>
                </Card>

                <PrimaryButton
                  type="submit"
                  className="w-full min-h-[48px]"
                  disabled={submitting}
                >
                  {submitting
                    ? isEditMode
                      ? 'Saving…'
                      : 'Creating…'
                    : isEditMode
                      ? 'Save Due'
                      : 'Create Due'}
                </PrimaryButton>
              </div>
            </form>
          </div>
        </div>
      </div>

      {isLineItemModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-xl p-0 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
              <div className="text-lg font-semibold text-gray-900">Add Line Item</div>
              <button
                type="button"
                onClick={closeAddLineItem}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={confirmAddLineItem} className="px-6 py-5 bg-[#EEF6E6]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Item Name
                  </label>
                  <Input
                    value={draftLineItem.name}
                    onChange={(e) => setDraftLineItem((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Enter Item Name"
                    className="min-h-[48px] bg-white"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Quantity
                  </label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={draftLineItem.quantity}
                    onChange={(e) => setDraftLineItem((p) => ({ ...p, quantity: e.target.value }))}
                    placeholder="Enter Quantity"
                    className="min-h-[48px] bg-white"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Unit
                  </label>
                  <Input
                    value={draftLineItem.unit}
                    onChange={(e) => setDraftLineItem((p) => ({ ...p, unit: e.target.value }))}
                    placeholder="month"
                    className="min-h-[48px] bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Price per Unit
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={draftLineItem.price_per_unit}
                    onChange={(e) =>
                      setDraftLineItem((p) => ({ ...p, price_per_unit: e.target.value }))
                    }
                    placeholder="1500"
                    className="min-h-[48px] bg-white"
                    required
                  />
                </div>
              </div>

              <div className="mt-6">
                <PrimaryButton type="submit" className="w-full min-h-[54px]">
                  Add Line Item
                </PrimaryButton>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CreateDue;

